import {ReactElement, useEffect, useMemo, useState} from "react";
import {Button} from "@zendeskgarden/react-buttons";
import {Checkbox, Field, Label, MediaInput} from "@zendeskgarden/react-forms";
import {ReactComponent as SearchIcon} from "@zendeskgarden/svg-icons/src/16/search-stroke.svg";
import {ReactComponent as AssignIcon} from "@zendeskgarden/svg-icons/src/16/chevron-double-right-fill.svg";
import {ReactComponent as UnassignIcon} from "@zendeskgarden/svg-icons/src/16/chevron-double-left-fill.svg";
import {Inline} from "@zendeskgarden/react-loaders";
import {useAddGprEntityGroupPropertyMutation} from "../../../__generated__/generated_types";
import {toast} from "react-toastify";
import {COLORS} from "../../../constants/Colors";
import * as css from "./styles/gprEntityStyles.module.scss";
import {itemId, useMultiSelect} from "../../../utils/hooks/useMultiSelect";

interface PropertyItem {
    id: string;
    name: string;
}

interface GPREGroupPropertiesViewProps {
    group: {
        id: string;
        name: string;
        properties: {id: string}[];
    };
    allProperties: PropertyItem[];
    allGroups: {
        id?: string | null;
        name: string;
        properties: {id: string}[];
    }[];
    budgetYear: number;
    closed: (changed: boolean) => void;
}

export function GPREGroupPropertiesView(props: GPREGroupPropertiesViewProps): ReactElement {
    const {group, allProperties, allGroups, budgetYear} = props;

    const [addGroupProperty] = useAddGprEntityGroupPropertyMutation();
    const [saving, setSaving] = useState(false);

    const originalAssignedIds = useMemo(() => {
        return new Set(group.properties.map(p => p.id));
    }, [group]);

    const propertyGroupMap = useMemo(() => {
        const map = new Map<string, string>();
        for(const g of allGroups) {
            if(g.id == null || g.id === group.id) {
                continue;
            }
            for(const property of g.properties) {
                if(!map.has(property.id)) {
                    map.set(property.id, g.name);
                }
            }
        }
        return map;
    }, [allGroups, group.id]);

    const [assignedProperties, setAssignedProperties] = useState<PropertyItem[]>(() => {
        return allProperties.filter(p => originalAssignedIds.has(p.id));
    });

    const [availableProperties, setAvailableProperties] = useState<PropertyItem[]>(() => {
        return allProperties.filter(p => !originalAssignedIds.has(p.id));
    });

    const [availableSearch, setAvailableSearch] = useState("");
    const [assignedSearch, setAssignedSearch] = useState("");
    const [hideAlreadyAssigned, setHideAlreadyAssigned] = useState(true);

    const [filteredAvailable, setFilteredAvailable] = useState<PropertyItem[]>(availableProperties);
    const [filteredAssigned, setFilteredAssigned] = useState<PropertyItem[]>(assignedProperties);
    const availableSelection = useMultiSelect(filteredAvailable, itemId);
    const assignedSelection = useMultiSelect(filteredAssigned, itemId);
    const selectedAvailableProperties = availableSelection.selected;
    const selectedAssignedProperties = assignedSelection.selected;

    useEffect(() => {
        let filtered = availableProperties;
        if(hideAlreadyAssigned) {
            filtered = filtered.filter(p => !propertyGroupMap.has(p.id));
        }
        if(availableSearch !== "") {
            filtered = filtered.filter(p =>
                p.name.toLowerCase().includes(availableSearch.toLowerCase())
            );
        }
        setFilteredAvailable(filtered);
    }, [availableSearch, availableProperties, hideAlreadyAssigned, propertyGroupMap]);

    useEffect(() => {
        if(assignedSearch === "") {
            setFilteredAssigned(assignedProperties);
        } else {
            setFilteredAssigned(assignedProperties.filter(p =>
                p.name.toLowerCase().includes(assignedSearch.toLowerCase())
            ));
        }
    }, [assignedSearch, assignedProperties]);

    function assignProperties(): void {
        availableSelection.setSelected([]);
        assignedSelection.setSelected(prev => [...selectedAvailableProperties, ...prev]);
        setAvailableProperties(prev => prev.filter(p => !selectedAvailableProperties.some(x => x.id === p.id)));
        setAssignedProperties(prev => [...selectedAvailableProperties, ...prev]);
    }

    function unassignProperties(): void {
        availableSelection.setSelected(prev => [...selectedAssignedProperties, ...prev]);
        assignedSelection.setSelected([]);
        setAvailableProperties(prev => [...selectedAssignedProperties, ...prev]);
        setAssignedProperties(prev => prev.filter(p => !selectedAssignedProperties.some(x => x.id === p.id)));
    }

    function doSave(): void {
        const currentAssignedIds = new Set(assignedProperties.map(p => p.id));
        const unchanged = currentAssignedIds.size === originalAssignedIds.size
            && Array.from(originalAssignedIds).every(id => currentAssignedIds.has(id));
        if(unchanged) {
            props.closed(false);
            return;
        }

        setSaving(true);
        addGroupProperty({
            variables: {
                budgetYear,
                groupId: group.id,
                propertyIds: assignedProperties.map(p => p.id)
            }
        }).then(() => {
            toast.success("Property associations updated!");
            props.closed(true);
        }).catch(() => {/* Do Nothing */})
            .finally(() => setSaving(false));
    }

    return <>
        <div className={css.tableHeaderActions}>
            <h5 className={css.tableHeaderActionsLeft}>
                Properties for &quot;{group.name}&quot;
                {saving && <Inline size={24} color={COLORS.PRIMARY_500} aria-label="loading"/>}
            </h5>
            <div className={css.tableHeaderActionsRight}>
                <Button onClick={() => props.closed(false)}>Cancel</Button>
                <Button isPrimary className={css.tableHeaderActionsButton} disabled={saving} onClick={doSave}>
                    Save
                </Button>
            </div>
        </div>
        <div className={css.manageViewBody}>
            <div className={css.dualListContainer}>
                <div className={css.dualListColumn}>
                    <div className={css.dualListHeader}>
                        <div className={css.dualListHeaderContent}>
                            <h4>Available</h4>
                            <Field className={css.hideAlreadyAssignedField}>
                                <Checkbox
                                    checked={hideAlreadyAssigned}
                                    onChange={e => setHideAlreadyAssigned(e.target.checked)}
                                >
                                    <Label>Hide already assigned</Label>
                                </Checkbox>
                            </Field>
                        </div>
                        <Field>
                            <Label hidden>Search Available</Label>
                            <MediaInput
                                type="text"
                                placeholder="Search..."
                                isCompact
                                start={<SearchIcon />}
                                value={availableSearch}
                                onChange={e => setAvailableSearch(e.target.value)}
                            />
                        </Field>
                        <div className={css.selectionStatusContainer}>
                            <Field>
                                <Checkbox
                                    checked={availableSelection.allSelected}
                                    indeterminate={availableSelection.someSelected}
                                    onChange={e => availableSelection.selectAll(e.target.checked)}
                                >
                                    <Label>
                                        <span>{selectedAvailableProperties.length} of {availableProperties.length} selected</span>
                                    </Label>
                                </Checkbox>
                            </Field>
                        </div>
                    </div>
                    <div className={css.dualListItems}>
                        {filteredAvailable.map((p, index) => {
                            const currentGroup = propertyGroupMap.get(p.id);
                            return <div key={p.id} className={css.listItem}>
                                <Field>
                                    <Checkbox
                                        checked={availableSelection.isSelected(p)}
                                        onClick={event => availableSelection.handleSelect(event.currentTarget.checked, p, index, event.shiftKey)}
                                        onChange={() => undefined}
                                    >
                                        <Label>
                                            <span className={css.itemName}>{p.name}</span>
                                            {currentGroup &&
                                                <span className={css.itemMeta}>Current: {currentGroup}</span>
                                            }
                                        </Label>
                                    </Checkbox>
                                </Field>
                            </div>;
                        })}
                    </div>
                </div>
                <div className={css.middleButtons}>
                    <Button onClick={assignProperties}><AssignIcon /></Button>
                    <Button onClick={unassignProperties}><UnassignIcon /></Button>
                </div>
                <div className={css.dualListColumn}>
                    <div className={css.dualListHeader}>
                        <div className={css.dualListHeaderContent}>
                            <h4>Assigned</h4>
                        </div>
                        <Field>
                            <Label hidden>Search Assigned</Label>
                            <MediaInput
                                type="text"
                                placeholder="Search..."
                                isCompact
                                start={<SearchIcon />}
                                value={assignedSearch}
                                onChange={e => setAssignedSearch(e.target.value)}
                            />
                        </Field>
                        <div className={css.selectionStatusContainer}>
                            <Field>
                                <Checkbox
                                    checked={assignedSelection.allSelected}
                                    indeterminate={assignedSelection.someSelected}
                                    onChange={e => assignedSelection.selectAll(e.target.checked)}
                                >
                                    <Label>
                                        <span>{selectedAssignedProperties.length} of {assignedProperties.length} selected</span>
                                    </Label>
                                </Checkbox>
                            </Field>
                        </div>
                    </div>
                    <div className={css.dualListItems}>
                        {filteredAssigned.map((p, index) =>
                            <div key={p.id} className={css.listItem}>
                                <Field>
                                    <Checkbox
                                        checked={assignedSelection.isSelected(p)}
                                        onClick={event => assignedSelection.handleSelect(event.currentTarget.checked, p, index, event.shiftKey)}
                                        onChange={() => undefined}
                                    >
                                        <Label>
                                            <span className={css.itemName}>{p.name}</span>
                                        </Label>
                                    </Checkbox>
                                </Field>
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </div>
    </>;
}
